import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QrCodeService } from '../../services/qr-code.service';
import { LoggerService } from '../../services/logger.service';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';

@Component({
  selector: 'app-qr-code-card',
  standalone: true,
  imports: [HlmSpinnerImports],
  templateUrl: './qr-code-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './qr-code-card.component.css',
})
export class QrCodeCardComponent implements OnInit, OnDestroy {
  @Input() storeName: string = '';
  @Input() storefrontUrl: string = '';
  @Input() showPrintButton: boolean = true;

  qrCodeSvg: string = '';
  cardSvg: string = '';
  safeCardSvg: SafeHtml = '';
  isLoading: boolean = true;
  error: string = '';
  copySuccess: boolean = false;
  displayUrl: string = '';

  constructor(
    private qrCodeService: QrCodeService,
    private sanitizer: DomSanitizer,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.formatDisplayUrl();
    this.generateQRCode();
  }

  ngOnDestroy() {
    // Clean up any resources if needed
  }

  private generateQRCode() {
    if (!this.storefrontUrl) {
      this.error = 'No storefront URL provided';
      this.isLoading = false;
      return;
    }

    if (this.storefrontUrl === '#') {
      this.error = 'Invalid storefront URL';
      this.isLoading = false;
      return;
    }

    try {
      this.qrCodeSvg = this.qrCodeService.generateQRCode(this.storefrontUrl, 168);

      if (this.qrCodeSvg && this.qrCodeSvg.length > 0) {
        this.generateCardSvg();
        this.isLoading = false;
        this.error = '';
      } else {
        this.error = 'QR code generation returned empty result';
        this.isLoading = false;
      }
    } catch (error) {
      this.error = 'Failed to generate QR code: ' + error;
      this.isLoading = false;
    }
  }

  private generateCardSvg() {
    // Generate a complete SVG card that includes everything
    const cardWidth = 400;
    const cardHeight = 580;
    const qrSize = 180;
    const padding = 20;

    // Try to extract the actual QR code SVG content instead of just path
    let qrCodeContent = '';
    if (this.qrCodeSvg) {
      // Extract the full SVG content and scale it properly
      const svgMatch = this.qrCodeSvg.match(/<svg[^>]*>(.*?)<\/svg>/s);
      if (svgMatch) {
        qrCodeContent = svgMatch[1];
      }
    }

    this.cardSvg = `
      <svg width="${cardWidth}" height="${cardHeight}" viewBox="0 0 ${cardWidth} ${cardHeight}" xmlns="http://www.w3.org/2000/svg">
        <!-- Definitions -->
        <defs>
          <!-- Card Shadow -->
          <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.1"/>
          </filter>
          
          <!-- QR Container Gradient -->
          <linearGradient id="qrBg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#f8fafc;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#ffffff;stop-opacity:1" />
          </linearGradient>
          
          <!-- Blue Gradient for Button -->
          <linearGradient id="blueBg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#4b5ae4;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#4338ca;stop-opacity:1" />
          </linearGradient>
        </defs>
        
        <!-- Card Background with Shadow -->
        <rect x="${padding}" y="${padding}" width="${cardWidth - 2 * padding}" height="${480}" 
              fill="white" stroke="#e5e7eb" stroke-width="1" rx="16" filter="url(#cardShadow)"/>
        
        <!-- Header Section Background -->
        <rect x="${padding}" y="${padding}" width="${cardWidth - 2 * padding}" height="120" 
              fill="#fafbfc" rx="16" ry="16"/>
        <rect x="${padding}" y="${padding + 80}" width="${cardWidth - 2 * padding}" height="20" 
              fill="#fafbfc"/>
        
        <!-- Store Name -->
        <text x="${cardWidth / 2}" y="85" text-anchor="middle" 
              font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" 
              font-size="32" font-weight="700" fill="#1f2937">
          ${this.storeName}
        </text>
        
        <!-- Subtitle -->
        <text x="${cardWidth / 2}" y="115" text-anchor="middle" 
              font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" 
              font-size="18" font-weight="600" fill="#4b5ae4">
          Digitale Gutscheine
        </text>
        
        <!-- QR Code Container -->
        <rect x="${(cardWidth - qrSize - 32) / 2}" y="160" width="${qrSize + 32}" height="${qrSize + 32}" 
              fill="url(#qrBg)" stroke="#d1d5db" stroke-width="2" rx="12"/>
        
        <!-- QR Code -->
        <g transform="translate(${(cardWidth - qrSize) / 2}, 176)">
          ${
            qrCodeContent
              ? `
            <rect width="${qrSize}" height="${qrSize}" fill="white" rx="4"/>
            <g transform="scale(${qrSize / 168})">
              ${qrCodeContent}
            </g>
          `
              : `
            <rect width="${qrSize}" height="${qrSize}" fill="white" stroke="#d1d5db" stroke-width="2" rx="8"/>
            <text x="${qrSize / 2}" y="${qrSize / 2 - 10}" text-anchor="middle" 
                  font-family="system-ui, sans-serif" font-size="16" font-weight="500" fill="#6b7280">
              QR Code
            </text>
            <text x="${qrSize / 2}" y="${qrSize / 2 + 10}" text-anchor="middle" 
                  font-family="system-ui, sans-serif" font-size="12" fill="#9ca3af">
              wird geladen...
            </text>
          `
          }
        </g>
        
        <!-- URL Display -->
        <text x="${cardWidth / 2}" y="410" text-anchor="middle" 
              font-family="system-ui, monospace" font-size="16" font-weight="500" fill="#4b5563">
          ${this.displayUrl}
        </text>
        
        <!-- Divider Line -->
        <line x1="40" y1="440" x2="${cardWidth - 40}" y2="440" stroke="#e5e7eb" stroke-width="1"/>
        
        <!-- Footer -->
        <g transform="translate(190, 470)">
          <!-- Powered by text -->
          <text x="-45" y="0" text-anchor="middle" 
                font-family="system-ui, sans-serif" font-size="12" fill="#9ca3af">
            powered by
          </text>
          
          <!-- Checkmark icon -->
          <circle cx="5" cy="-4" r="10" fill="#4b5ae4"/>
          <path d="M1,-4 L4,-1 L9,-8" 
                stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          
          <!-- Schenkwerk text -->
          <text x="25" y="0" 
                font-family="system-ui, sans-serif" font-size="16" font-weight="700" fill="#4b5ae4">
            schenkwerk
          </text>
        </g>
      </svg>
    `;

    this.safeCardSvg = this.sanitizer.bypassSecurityTrustHtml(this.cardSvg);
  }

  printQRCode() {
    if (!this.cardSvg) {
      this.logger.error('No card SVG available for printing');
      return;
    }

    // Create a new window with just the SVG card
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (printWindow) {
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>QR-Code - ${this.storeName}</title>
          <meta charset="UTF-8">
          <style>
            body {
              margin: 0;
              padding: 20px;
              background: white;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            svg {
              max-width: 100%;
              height: auto;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              svg {
                width: 100%;
                height: auto;
              }
            }
          </style>
        </head>
        <body>
          ${this.cardSvg}
        </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }, 100);
      };
    }
  }

  /**
   * Formats the URL for user-friendly display
   * Removes http:// and https:// protocols
   */
  private formatDisplayUrl() {
    if (!this.storefrontUrl || this.storefrontUrl === '#') {
      this.displayUrl = '';
      return;
    }

    let formattedUrl = this.storefrontUrl;

    // Remove protocol (http:// or https://)
    formattedUrl = formattedUrl.replace(/^https?:\/\//, '');

    this.displayUrl = formattedUrl;
  }

  copyUrl() {
    if (navigator.clipboard && this.storefrontUrl) {
      navigator.clipboard
        .writeText(this.storefrontUrl)
        .then(() => {
          this.copySuccess = true;
          // Hide success message after 2 seconds
          setTimeout(() => {
            this.copySuccess = false;
          }, 2000);
        })
        .catch((err) => {
          this.logger.error('Failed to copy URL:', err);
        });
    }
  }
}
